import { observer } from "mobx-react";
import { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartTooltip } from "./PieChartToolTip";
import { CustomLabel } from "./CustomLabel";
import { PieChartItem, pieChartProps } from "./shared";
import { Text } from "@chakra-ui/react";
import { t } from "i18next";
import { PresenceType, TreatmentType } from "@keita/lanepatrol-sdk";

import { AttributeUtils } from "../../../../coding/utils/attribute-utils";
import {
  mapGradientEnd,
  mapGradientStart,
} from "../../../../map/utils/map-utils";
import { useStores } from "../../../../shared/stores/RootStore";

interface Props {
  treatmentType: TreatmentType;
}
const TreatmentPieChartBase: React.FC<Props> = ({ treatmentType }) => {
  const { filterStore } = useStores();

  const data = useMemo(() => {
    if (!filterStore.filteredNodes.length) return;

    const totalNodes = filterStore.filteredNodes.length;
    var totalPresent = 0;
    var totalNotPresent = 0;
    var totalUncoded = 0;

    filterStore.filteredNodes.forEach((node) => {
      if (!node.score) {
        totalUncoded++;
      } else if (node.treatments?.find((t) => t.type === treatmentType)) {
        totalPresent++;
      } else {
        totalNotPresent++;
      }
    });

    const toPercent = (n: number) =>
      ((n / totalNodes) * filterStore.filteredDistance) / 1000;

    return [
      {
        name: AttributeUtils.getOptionLabel(PresenceType.Present),
        value: toPercent(totalPresent),
        color: mapGradientStart,
      },
      {
        name: AttributeUtils.getOptionLabel(PresenceType.NotPresent),
        value: toPercent(totalNotPresent),
        color: mapGradientEnd,
      },
      {
        name: t(`coding:null`),
        value: toPercent(totalUncoded),
        color: "#000",
      },
    ].filter((item) => item.value) as PieChartItem[];

    // eslint-disable-next-line
  }, [filterStore.filteredNodes, treatmentType]);

  if (!data)
    return (
      <Text fontSize="xs" alignSelf={"start"}>
        {t(`map:pieChart.noData`)}
      </Text>
    );

  return (
    <PieChart {...pieChartProps}>
      <Tooltip content={<PieChartTooltip />} />

      <Pie
        nameKey="name"
        dataKey="value"
        label={CustomLabel}
        data={data}
        outerRadius={80}
      >
        {data.map((item, i) => (
          <Cell key={i} fill={item.color} />
        ))}
      </Pie>
    </PieChart>
  );
};

export const TreatmentPieChart = observer(TreatmentPieChartBase);
